import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  archiveConvention,
  createConvention,
  deleteConvention,
  fetchConventionHistoryById,
  fetchConventionById,
  fetchConventionHistory,
  fetchConventionUsageSummary,
  fetchConventions,
  fetchConventionsPaged,
  restoreConventionHistory,
  type Convention,
  type ConventionHistoryDetail,
  type ConventionListItem,
  type ConventionUsageSummary,
  type PagedData,
  unarchiveConvention,
  updateConvention,
} from '../../lib/api';
import { type ConventionQueryParams, queryKeys } from './queryKeys';

type CreateConventionInput = {
  title: string;
  category: string;
  content: string;
  fileName?: string | null;
  trigger?: string | null;
  description?: string | null;
  agentInstruction?: string | null;
  scope?: string;
  tags?: string[];
};

type UpdateConventionInput = {
  updatedAt: string;
  title?: string;
  category?: string;
  content?: string;
  fileName?: string | null;
  trigger?: string | null;
  description?: string | null;
  agentInstruction?: string | null;
  scope?: string;
  tags?: string[];
};

export const useConventions = (
  token: string | null,
  projectId: string,
  params: ConventionQueryParams = {},
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: queryKeys.conventions.list(projectId, params),
    queryFn: () => fetchConventions(token as string, projectId, params),
    enabled: Boolean(token) && projectId.length > 0 && (options?.enabled ?? true),
    staleTime: 180_000,
    // 러너 모달 재오픈·프로젝트 전환 대응: 캐시 오래 유지 + 이전 목록 즉시 표시.
    gcTime: 1_800_000,
    placeholderData: keepPreviousData,
  });
};

type ConventionsInfiniteParams = Omit<ConventionQueryParams, 'page' | 'pageSize'>;

export const useConventionsInfinite = (
  token: string | null,
  projectId: string,
  params: ConventionsInfiniteParams = {},
  options?: { enabled?: boolean },
) => {
  return useInfiniteQuery<
    PagedData<ConventionListItem>,
    Error,
    PagedData<ConventionListItem>,
    ReturnType<typeof queryKeys.conventions.infinite>,
    number
  >({
    queryKey: queryKeys.conventions.infinite(projectId, params),
    queryFn: ({ pageParam }) =>
      fetchConventionsPaged(token as string, projectId, { ...params, page: pageParam, pageSize: 20 }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.meta.page < lastPage.meta.totalPages ? lastPage.meta.page + 1 : undefined,
    enabled: Boolean(token) && projectId.length > 0 && (options?.enabled ?? true),
    staleTime: 180_000,
    select: (data) =>
      ({
        pages: data.pages,
        pageParams: data.pageParams,
        data: data.pages.flatMap((p) => p.data),
        meta: data.pages[data.pages.length - 1]?.meta,
      }) as unknown as PagedData<ConventionListItem>,
  });
};

export const useConventionUsageSummary = (
  token: string | null,
  projectId: string,
  options: { from?: string; to?: string } = {},
) => {
  return useQuery<ConventionUsageSummary>({
    queryKey: queryKeys.conventions.usageSummary(projectId, options),
    queryFn: () => fetchConventionUsageSummary(token as string, projectId, options),
    enabled: Boolean(token) && projectId.length > 0,
    staleTime: 60_000,
  });
};

export const useConvention = (token: string | null, projectId: string, conventionId: string) => {
  return useQuery({
    queryKey: queryKeys.conventions.detail(projectId, conventionId),
    queryFn: () => fetchConventionById(token as string, projectId, conventionId),
    enabled: Boolean(token) && projectId.length > 0 && conventionId.length > 0,
    staleTime: 180_000,
  });
};

export const useConventionHistory = (token: string | null, projectId: string, conventionId: string) => {
  return useQuery({
    queryKey: queryKeys.conventions.history(projectId, conventionId),
    queryFn: () => fetchConventionHistory(token as string, projectId, conventionId),
    enabled: Boolean(token) && projectId.length > 0 && conventionId.length > 0,
    staleTime: 180_000,
  });
};

export const useConventionHistoryItem = (
  token: string | null,
  projectId: string,
  conventionId: string,
  historyId: string | null,
) => {
  return useQuery<ConventionHistoryDetail>({
    queryKey: queryKeys.conventions.historyDetail(projectId, conventionId, historyId ?? ''),
    queryFn: () => fetchConventionHistoryById(token as string, projectId, conventionId, historyId as string),
    enabled: Boolean(token) && projectId.length > 0 && conventionId.length > 0 && Boolean(historyId),
    staleTime: 180_000,
  });
};

export const useCreateConventionMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateConventionInput) => createConvention(token as string, projectId, input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conventions.all(projectId) });
    },
  });
};

export const useUpdateConventionMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  const updateConventionListItems = (items: ConventionListItem[], id: string, input: UpdateConventionInput) => {
    return items.map((item) => {
      if (item.id !== id) {
        return item;
      }

      return {
        ...item,
        title: input.title ?? item.title,
        category: input.category ?? item.category,
        fileName: input.fileName !== undefined ? input.fileName : item.fileName,
        trigger: input.trigger !== undefined ? input.trigger : item.trigger,
        description: input.description !== undefined ? input.description : item.description,
        agentInstruction: input.agentInstruction !== undefined ? input.agentInstruction : item.agentInstruction,
        scope: input.scope ?? item.scope,
        tags: input.tags ?? item.tags,
      };
    });
  };

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateConventionInput }) =>
      updateConvention(token as string, projectId, id, input),
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.conventions.all(projectId) });

      const previousLists = queryClient.getQueriesData<ConventionListItem[] | PagedData<ConventionListItem>>({
        queryKey: queryKeys.conventions.lists(projectId),
      });
      const previousDetail = queryClient.getQueryData<Convention>(queryKeys.conventions.detail(projectId, id));

      queryClient.setQueriesData<ConventionListItem[] | PagedData<ConventionListItem>>(
        { queryKey: queryKeys.conventions.lists(projectId) },
        (current) => {
          if (!current) {
            return current;
          }

          if (Array.isArray(current)) {
            return updateConventionListItems(current, id, input);
          }

          return {
            ...current,
            data: updateConventionListItems(current.data, id, input),
          };
        },
      );

      queryClient.setQueryData<Convention>(queryKeys.conventions.detail(projectId, id), (current) => {
        if (!current) {
          return current;
        }

        return {
          ...current,
          title: input.title ?? current.title,
          category: input.category ?? current.category,
          content: input.content ?? current.content,
          contentPlainText: input.content ?? current.contentPlainText,
          fileName: input.fileName !== undefined ? input.fileName : current.fileName,
          trigger: input.trigger !== undefined ? input.trigger : current.trigger,
          description: input.description !== undefined ? input.description : current.description,
          agentInstruction: input.agentInstruction !== undefined ? input.agentInstruction : current.agentInstruction,
          scope: input.scope ?? current.scope,
        };
      });

      return { previousLists, previousDetail, id };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      for (const [queryKey, previousData] of context.previousLists) {
        queryClient.setQueryData(queryKey, previousData);
      }

      queryClient.setQueryData(queryKeys.conventions.detail(projectId, context.id), context.previousDetail);
    },
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conventions.all(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conventions.detail(projectId, variables.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conventions.history(projectId, variables.id) });
    },
  });
};

export const useDeleteConventionMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteConvention(token as string, projectId, id),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conventions.all(projectId) });
    },
  });
};

export const useArchiveConventionMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived
        ? archiveConvention(token as string, projectId, id)
        : unarchiveConvention(token as string, projectId, id),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conventions.all(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conventions.detail(projectId, variables.id) });
    },
  });
};

export const useRestoreConventionHistoryMutation = (token: string | null, projectId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conventionId, historyId }: { conventionId: string; historyId: string }) =>
      restoreConventionHistory(token as string, projectId, conventionId, historyId),
    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conventions.all(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.conventions.detail(projectId, variables.conventionId) });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conventions.history(projectId, variables.conventionId),
      });
    },
  });
};
